// Travel times (SPEC §4.11, §7.7): one row per pair of non-archived centers; blank uses the default.

import type { ApiResult } from "../../api/client";
import { fetchCenters, fetchTravelTimes, saveTravelTimes } from "../../api/centers";
import type { Center, TravelEntry, TravelTimes } from "../../api/types";
import { plural, t } from "../../i18n/i18n";
import { setRedraw } from "../../router";
import { isAdmin } from "../../session";
import { createButton, createElement } from "../../ui/dom";
import { reportAndReload } from "../../ui/resultMessage";
import { createLoading, showPageProblem, startPage } from "../pageFrame";

interface PairRow {
  first: Center;
  second: Center;
  input: HTMLInputElement;
}

export async function renderTravelTimesPage(container: HTMLElement): Promise<void> {
  const page: HTMLElement = startPage(container, t("admin.travelTimes"));
  page.append(createLoading());
  const centers: ApiResult<Center[]> = await fetchCenters(false);
  const travel: ApiResult<TravelTimes> = await fetchTravelTimes();
  if (centers.ok === false) {
    showPageProblem(page, centers.problem);
    return;
  }

  if (travel.ok === false) {
    showPageProblem(page, travel.problem);
    return;
  }

  const draw = (): void => {
    drawPage(startPage(container, t("admin.travelTimes")), centers.data, travel.data);
  };
  setRedraw(container, draw);
  draw();
}

function drawPage(page: HTMLElement, centers: Center[], travel: TravelTimes): void {
  page.append(createElement("p", "muted", t("admin.travelHelp", { minutes: travel.defaultMinutes })));
  const rows: PairRow[] = buildPairs(centers, travel.entries);
  const table: HTMLTableElement = createElement("table");
  const body: HTMLTableSectionElement = table.createTBody();
  for (const row of rows) {
    const tableRow: HTMLTableRowElement = body.insertRow();
    const cell: HTMLTableCellElement = createElement("td");
    cell.append(isAdmin() ? row.input : describeMinutes(row.input.value, travel.defaultMinutes));
    tableRow.append(createElement("td", "", `${row.first.name} ↔ ${row.second.name}`), cell);
  }

  page.append(table);
  if (isAdmin()) {
    page.append(createButton(t("app.save"), async (): Promise<void> => {
      await reportAndReload(await saveTravelTimes(readEntries(rows), travel.version));
    }, "button-primary"));
  }
}

function buildPairs(centers: Center[], entries: TravelEntry[]): PairRow[] {
  const rows: PairRow[] = [];
  centers.forEach((first: Center, index: number): void => {
    for (const second of centers.slice(index + 1)) {
      const entry: TravelEntry | undefined = entries.find((candidate: TravelEntry): boolean =>
        (candidate.centerIdA === first.id && candidate.centerIdB === second.id) ||
        (candidate.centerIdA === second.id && candidate.centerIdB === first.id));
      rows.push({ first, second, input: createMinutesInput(entry?.minutes ?? null) });
    }
  });
  return rows;
}

function createMinutesInput(minutes: number | null): HTMLInputElement {
  const input: HTMLInputElement = createElement("input");
  input.type = "number";
  input.min = "0";
  input.max = "240";
  input.step = "5";
  input.value = minutes === null ? "" : String(minutes);
  return input;
}

/** Read-only text for viewers: the minutes, or the default when blank. */
function describeMinutes(value: string, defaultMinutes: number): string {
  return value === "" ? `${plural("minutes", defaultMinutes)} *` : plural("minutes", Number(value));
}

function readEntries(rows: PairRow[]): TravelEntry[] {
  return rows
    .filter((row: PairRow): boolean => row.input.value.trim() !== "")
    .map((row: PairRow): TravelEntry => ({
      centerIdA: row.first.id, centerIdB: row.second.id, minutes: Number(row.input.value),
    }));
}
